// Delivery audit panel — what the AI auto-deliver cron has actually
// sent on the operator's behalf. Reads email_digest_log where
// kind='policy_delivery' (the rows the cron writes after a successful
// Resend send) and renders them most-recent first.
//
// Lives in Settings as a transparency surface for the operator: if
// they ever wonder "did the AI send to that client?" they have a
// single place to check instead of guessing. Auto-hides when there's
// nothing to show.

import React from 'react';
import { supabase } from '../lib/supabase';

const LIMIT = 50;

function fmtAt(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

function statusBadge(status) {
  if (status === 'sent') {
    return <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Sent</span>;
  }
  if (status === 'dry_run') {
    return <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-50 text-slate-600 border border-slate-200">Dry-run</span>;
  }
  return <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">Failed</span>;
}

export default function DeliveryAuditPanel({ currentUser }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('email_digest_log')
        .select('id, kind, period_key, recipient_email, subject, metrics, status, error, created_at')
        .eq('owner_id', currentUser.id)
        .eq('kind', 'policy_delivery')
        .order('created_at', { ascending: false })
        .limit(LIMIT);
      if (cancelled) return;
      setRows(Array.isArray(data) ? data : []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id]);

  if (loading || rows.length === 0) return null;

  const sentCount = rows.filter(r => r.status === 'sent').length;
  const failedCount = rows.filter(r => r.status === 'failed').length;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="font-bold mb-1">AI auto-delivery — recent activity</h3>
      <p className="text-sm text-slate-500 mb-3">
        The last {rows.length} Policy Delivery Summaries the AI sent on your behalf. {sentCount} delivered, {failedCount} failed.
      </p>
      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
        {rows.map(r => (
          <div key={r.id} className="px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-900 truncate">{r.recipient_email}</div>
              <div className="text-xs text-slate-500 truncate">
                {r.subject}
                {r.metrics?.carrier && <span> · {r.metrics.carrier}</span>}
                {r.metrics?.policy  && <span> · {r.metrics.policy}</span>}
              </div>
              {r.error && <div className="text-xs text-red-700 mt-0.5 truncate" title={r.error}>{r.error}</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {statusBadge(r.status)}
              <span className="text-xs text-slate-500 tabular-nums">{fmtAt(r.created_at)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
